import React from 'react'

type UseProductButtonTooltipProps = {
  isUnavailable: boolean
  qty: number
  stepperIncrementLabel: string
  tooltipMessage: string
}

const getStepperIncrementButton = (target: EventTarget | null, incrementLabel: string) => {
  if (!(target instanceof Element)) {
    return null
  }

  const button = target.closest('button')

  return button?.textContent?.includes(incrementLabel) ? button : null
}

const useProductButtonTooltip = ({
  isUnavailable,
  qty,
  stepperIncrementLabel,
  tooltipMessage,
}: UseProductButtonTooltipProps) => {
  const [visible, setVisible] = React.useState(false)
  const targetRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isUnavailable || !tooltipMessage) {
      setVisible(false)
    }
  }, [isUnavailable, tooltipMessage])

  React.useEffect(() => {
    if (!visible) {
      return undefined
    }

    const handleOutsideTouch = (event: TouchEvent) => {
      const target = event.target

      if (target instanceof Node && targetRef.current?.contains(target)) {
        return
      }

      setVisible(false)
    }
    const hideTooltip = () => setVisible(false)

    document.addEventListener('touchstart', handleOutsideTouch)
    document.addEventListener('scroll', hideTooltip, true)

    return () => {
      document.removeEventListener('touchstart', handleOutsideTouch)
      document.removeEventListener('scroll', hideTooltip, true)
    }
  }, [visible])

  const handleVisibilityChange = React.useCallback(
    (
      nextVisible: boolean,
      target: EventTarget | null,
      relatedTarget: EventTarget | null = null,
    ) => {
      const incrementButton =
        qty > 0 ? getStepperIncrementButton(target, stepperIncrementLabel) : null

      if (qty > 0 && !incrementButton) {
        return
      }

      if (!nextVisible && relatedTarget instanceof Node) {
        const tooltipTarget = incrementButton || targetRef.current

        if (tooltipTarget?.contains(relatedTarget)) {
          return
        }
      }

      if (isUnavailable && tooltipMessage) {
        setVisible(nextVisible)
      }
    },
    [isUnavailable, qty, stepperIncrementLabel, tooltipMessage],
  )

  return {
    handleVisibilityChange,
    targetRef,
    visible: isUnavailable && visible && Boolean(tooltipMessage),
  }
}

export { useProductButtonTooltip }
